/** Short how-tos per role, shown in Help → Quick guide. Keep each step one line. */
export interface Guide {
  title: string;
  steps: string[];
  link?: { label: string; path: string };
}

const common: Guide[] = [
  {
    title: 'Find anything fast',
    steps: ['Press Ctrl+K (⌘K on Mac) or click Search at the top.', 'Type a patient name, mobile number or prescription number.'],
  },
  {
    title: 'Work in another branch',
    steps: ['Click the hospital name at the top and pick the branch.', 'Everything you see then belongs to that branch.'],
  },
];

export const GUIDES: Record<string, Guide[]> = {
  doctor: [
    {
      title: 'Write and sign a prescription',
      steps: [
        'Open the appointment (or Prescriptions → New) and pick the patient.',
        'Use Quick start: Repeat last, one of your templates, or Specialty templates.',
        'Speak instead of typing: tap the mic on Notes (English, हिन्दी or मराठी).',
        'Add lab tests at the bottom — they go straight to your lab.',
        'Press Save & sign. Add your registration number in Settings first.',
      ],
      link: { label: 'New prescription', path: '/prescriptions/create' },
    },
    {
      title: 'Send it to the patient',
      steps: ['Open the signed prescription and press Send on WhatsApp.', 'Or send the patient their Health records link from the patient page.'],
    },
    { title: 'Set your working hours', steps: ['My Availability → add the days and times you see patients.', 'Online booking only offers these times.'], link: { label: 'My availability', path: '/doctor/availability' } },
    ...common,
  ],
  receptionist: [
    {
      title: 'Check a patient in',
      steps: ['On the dashboard, find today\'s appointment and press the tick.', 'The patient gets a token number for the waiting room screen.'],
    },
    {
      title: 'Bill and collect',
      steps: ['Press ₹ on the appointment, or Billing → New bill.', 'Pick Cash, UPI (shows a QR with the amount) or Card.', 'Refunds: Billing → the ↩ button on the bill.', 'End of day: Billing → Day closing, then count the cash.'],
      link: { label: 'Billing', path: '/billing' },
    },
    { title: 'Online bookings', steps: ['Bookings from your booking page show an "Online" tag.', 'Press the WhatsApp button to confirm the time with the patient.'] },
    ...common,
  ],
  admin: [
    {
      title: 'Get the hospital ready',
      steps: ['Follow the checklist on your dashboard.', 'Hospital Settings: letterhead, fees, GSTIN and UPI ID.', 'Staff: invite doctors and front desk by email (they sign in with Google).'],
      link: { label: 'Hospital settings', path: '/admin/settings' },
    },
    {
      title: 'Take bookings online',
      steps: ['Dashboard → Online booking: switch it on and choose the page address.', 'Share the link on WhatsApp or print the QR for the counter.'],
    },
    {
      title: 'Patient privacy (DPDP)',
      steps: ['Patient → Privacy: record consent, download their data, see who opened the record.', 'Only admins can erase a patient\'s personal details.'],
    },
    { title: 'Pharmacy', steps: ['Pharmacy → Switch on, then Receive stock when medicines arrive.', 'Low stock and medicines expiring within 60 days are flagged.'], link: { label: 'Pharmacy', path: '/pharmacy' } },
    ...common,
  ],
  lab_technician: [
    { title: 'Enter results', steps: ['Lab Reports → To do lists tests doctors ordered.', 'Open one, fill the results (normal ranges are pre-filled), press Save report.', 'Abnormal values are flagged for the pathologist.'], link: { label: 'Lab reports', path: '/lab-reports' } },
    ...common,
  ],
  pathologist: [
    { title: 'Review and sign', steps: ['Reports to review lists results waiting for you.', 'Open one, add remarks if needed, press Review & sign.', 'The signed PDF can be sent to the patient on WhatsApp.'] },
    ...common,
  ],
};

export const guidesFor = (role?: string): Guide[] => GUIDES[String(role ?? '').toLowerCase()] ?? common;
